import { Academy, AcademyCertificateBranding, ApiResponse, Coupon, Notification } from '@court-sync/shared';
import type {
  AcademyOperatingProfileInput,
  AcademySport,
  UpdateAcademySportsPayload,
} from '@court-sync/shared';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, publicApiGet } from './apiClient';
import { parseBrandedSubdomainBlock, pathSubpathForAcademySlug, redirectToBrandedSubdomain } from '@/lib/brandedSubdomainRedirect';
import type { PublicOrganizationProfile } from './publicOrg';

function isPublicOrganizationProfile(data: unknown): data is PublicOrganizationProfile {
  return (
    typeof data === 'object' &&
    data !== null &&
    'organization' in data &&
    'modules' in data
  );
}

/** Unified org profile responses embed the academy; legacy responses return Academy directly. */
export function resolveAcademyFromPublicSlugResponse(data: unknown): Academy {
  if (isPublicOrganizationProfile(data)) {
    if (!data.academy) {
      throw new Error('Academy not found');
    }
    return data.academy;
  }
  return data as Academy;
}

export type DashboardPeriodKind = 'term' | 'month';

export interface DashboardPeriod {
  kind: DashboardPeriodKind;
  label: string;
  startDate: string;
  endDate: string;
  termId?: string | null;
}

export interface PeriodStats {
  period: DashboardPeriod;
  revenuePotential: number;
  lessons: number;
  students: number;
  bookings: number;
  /** Present when period.kind is `term` (backward compatibility). */
  termId: string | null;
  termName: string | null;
}

export interface DashboardOverviewStats {
  current: PeriodStats;
  previous: PeriodStats;
}

export const academyService = {
  async getAcademies(): Promise<ApiResponse<Academy[]>> {
    return apiGet<Academy[]>('/academies');
  },

  async getUserAcademies(): Promise<ApiResponse<Academy[]>> {
    return apiGet<Academy[]>('/academies/user');
  },

  async getAcademy(id: string): Promise<ApiResponse<Academy>> {
    return apiGet<Academy>(`/academies/${id}`);
  },

  async getCertificateBranding(academyId: string): Promise<AcademyCertificateBranding> {
    const response = await apiGet<AcademyCertificateBranding>(
      `/academies/${academyId}/certificate-branding`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to load certificate branding');
  },

  async createAcademy(academyData: Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt' | 'ownerId'>> & Pick<Academy, 'name' | 'slug'>): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>('/academies', academyData);
  },

  async updateAcademy(
    id: string,
    academyData: Partial<Omit<Academy, 'id' | 'createdAt' | 'updatedAt'>> & {
      acknowledgeSurchargePassThrough?: true;
    }
  ): Promise<ApiResponse> {
    return apiPut(`/academies/${id}`, academyData);
  },

  async deleteAcademy(id: string): Promise<ApiResponse> {
    return apiDelete(`/academies/${id}`);
  },

  async getPublicAcademies(): Promise<ApiResponse<Academy[]>> {
    return publicApiGet<Academy[]>('/public/academies');
  },

  async getAcademyBySlug(
    slug: string,
    options?: { redirectSubpath?: string | null }
  ): Promise<Academy> {
    const redirectSubpath =
      options?.redirectSubpath === null
        ? undefined
        : options?.redirectSubpath ??
          (typeof window !== 'undefined'
            ? pathSubpathForAcademySlug(window.location.pathname, slug)
            : undefined);

    try {
      const response = await publicApiGet<Academy | PublicOrganizationProfile>(`/public/academies/${slug}`);
      if (response.success && response.data) {
        return resolveAcademyFromPublicSlugResponse(response.data);
      }
      throw new Error(response.error || 'Failed to fetch academy');
    } catch (error) {
      const block = parseBrandedSubdomainBlock(error);
      if (block && redirectSubpath !== undefined) {
        redirectToBrandedSubdomain(block, redirectSubpath);
        throw new Error('Redirecting to branded subdomain');
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to fetch academy');
    }
  },

  /** Authenticated slug lookup for member portals (not blocked when branded subdomain is active). */
  async resolveAcademyBySlug(slug: string): Promise<Academy> {
    const response = await apiGet<Academy>(`/academies/by-slug/${encodeURIComponent(slug)}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch academy');
  },

  async getAcademyBySubdomain(label: string): Promise<Academy> {
    const response = await publicApiGet<Academy | PublicOrganizationProfile>(
      `/public/academies/subdomain/${encodeURIComponent(label)}`
    );
    if (response.success && response.data) {
      return resolveAcademyFromPublicSlugResponse(response.data);
    }
    throw new Error(response.error || 'Failed to fetch academy');
  },

  async checkSubdomainAvailability(
    label: string,
    excludeAcademyId?: string
  ): Promise<{ available: boolean; label: string }> {
    const params = excludeAcademyId ? { excludeAcademyId } : undefined;
    const response = await publicApiGet<{ available: boolean; label: string }>(
      `/public/academies/subdomain/${encodeURIComponent(label)}/check`,
      { params }
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to check subdomain');
  },

  async updateSubdomainSettings(
    academyId: string,
    data: { subdomain: string; subdomainEnabled: boolean }
  ): Promise<ApiResponse<Academy>> {
    return apiPut<Academy>(`/academies/${academyId}/subdomain-settings`, data);
  },

  async getAcademyCoupons(academyId: string): Promise<ApiResponse<Coupon[]>> {
    return apiGet<Coupon[]>(`/academies/${academyId}/coupons`);
  },

  async createAcademyCoupon(
    academyId: string,
    data: {
      code: string;
      type: 'percent' | 'fixed';
      value: number;
      validFrom?: string;
      validUntil?: string;
      maxRedemptions?: number;
    }
  ): Promise<ApiResponse<Coupon>> {
    return apiPost<Coupon>(`/academies/${academyId}/coupons`, data);
  },

  async updateAcademyCoupon(
    academyId: string,
    couponId: string,
    data: { validFrom?: string; validUntil?: string; maxRedemptions?: number }
  ): Promise<ApiResponse<Coupon>> {
    return apiPatch<Coupon>(`/academies/${academyId}/coupons/${couponId}`, data);
  },

  async getAcademySubscriptionPlan(slug: string): Promise<{ tier: string; hasPaymentProcessing: boolean }> {
    const response = await publicApiGet<{ tier: string; hasPaymentProcessing: boolean }>(
      `/public/academies/${slug}/subscription-plan`
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch subscription plan');
  },

  async getDashboardStats(academyId: string): Promise<ApiResponse<DashboardOverviewStats>> {
    return apiGet<DashboardOverviewStats>(`/academies/${academyId}/dashboard-stats`);
  },

  async getSetupChecklistProgress(
    academyId: string
  ): Promise<ApiResponse<{ done: Record<string, boolean>; completedAt: string | null }>> {
    return apiGet<{ done: Record<string, boolean>; completedAt: string | null }>(
      `/academies/${academyId}/setup-checklist`
    );
  },

  async getRecentActivity(academyId: string, limit = 15): Promise<ApiResponse<Notification[]>> {
    return apiGet<Notification[]>(`/academies/${academyId}/activity`, {
      params: { limit },
    });
  },

  async getAcademySports(academyId: string): Promise<
    ApiResponse<{
      sports: AcademySport[];
      operatingProfile?: AcademyOperatingProfileInput | null;
    }>
  > {
    return apiGet(`/academies/${academyId}/sports`);
  },

  async updateAcademySports(
    academyId: string,
    payload: UpdateAcademySportsPayload
  ): Promise<ApiResponse<{ sports: AcademySport[] }>> {
    return apiPut(`/academies/${academyId}/sports`, payload);
  },

  async updateOperatingProfile(
    academyId: string,
    profile: Partial<AcademyOperatingProfileInput>
  ): Promise<ApiResponse> {
    return apiPut(`/academies/${academyId}/operating-profile`, profile);
  },
};
